// Dependencies
import React, { useEffect } from 'react';
import { useActionData, useSubmit, useRevalidator, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Styles
import {
  ContainerForm,
  ContainerOra,
  LinkPassword,
  LinkPolicy,
  OraLogo,
  InputTextPassword,
  PrincipalButton,
  Title,
  TextInput,
  ContainerLinkPassword,
} from './styles';

import LogoOra from '../../../assets/logo.svg';

// Components
import { Typography } from '../../display/Typography';

// Schemas
import { LoginFormSchema } from './schema';

// Types
import { LoginFormType } from './types';

// Utils
import { createFormDataFromValues } from '../../../utils/forms';
import { requestSavedCredentials } from '../../../utils/savedCredentials';
import { getQueueCounts } from '../../../offline/queueCounts';
import { sessionEndedNoticeKey } from './sessionEndedNotice';
import { dismissSessionEndedToast, showSessionEndedToast } from '../../../utils/toasts';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

export default function LoginForm() {
  const { t } = useTranslation('auth');

  // Motivo del cierre de sesión que decidió el servidor (spec 2026-10-01 § 5.4).
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');

  // Aviso del cierre en un toast. Si quedó trabajo en cola, se dice que se
  // enviará al volver a entrar (por eso se espera a contar la cola).
  useEffect(() => {
    // Solo con un motivo de sesión terminada: otros `reason` no muestran aviso.
    if (sessionEndedNoticeKey(reason, false) === null) return;
    let cancelled = false;
    getQueueCounts()
      .then((c) => c.pending + c.failed + c.foreign > 0)
      .catch(() => false)
      .then((hasQueuedWork) => {
        if (cancelled) return;
        const key = sessionEndedNoticeKey(reason, hasQueuedWork);
        if (key) showSessionEndedToast(t(key));
      });
    return () => {
      cancelled = true;
    };
  }, [reason, t]);
  useEffect(() => () => dismissSessionEndedToast(), []);

  const isLandscapeTablet = useIsLandscapeTablet();
  const errorsAction = useActionData() as null | string;
  const revalidator = useRevalidator();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors, touchedFields },
    reset,
  } = useForm<LoginFormType>({
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(LoginFormSchema) as any as any,
  });

  const submit = useSubmit();

  /**
   * Rellenar con la credencial guardada en el gestor del sistema.
   *
   * Solo hace algo dentro de la app nativa; en navegador y PWA
   * `requestSavedCredentials` devuelve `null` sin tocar nada, así que esta
   * pantalla se comporta exactamente igual que antes.
   *
   * Rellena pero NO envía: que el operario vea qué usuario va a entrar antes de
   * pulsar. En una tablet compartida, entrar solo con quien la usó la última vez
   * es justo el fallo que hay que evitar.
   */
  useEffect(() => {
    let discarded = false;

    void requestSavedCredentials().then((credential) => {
      if (!credential || discarded) return;
      setValue('email', credential.username, { shouldValidate: true });
      setValue('password', credential.password, { shouldValidate: true });
    });

    return () => {
      discarded = true;
    };
  }, [setValue]);

  useEffect(() => {
    if (
      typeof errorsAction === 'string' &&
      errorsAction.length > 0 &&
      (touchedFields?.email || touchedFields?.password)
    ) {
      revalidator.revalidate();
    }
  }, [errorsAction, touchedFields?.email, touchedFields?.password]);

  const onFormSubmit = (values: LoginFormType) => {
    const formData = createFormDataFromValues(values);

    submit(formData, {
      method: 'post',
      action: '/login',
    });
    reset(values);
  };

  return (
    <>
      <ContainerOra>
        <OraLogo src={LogoOra} alt={t('auth:login.roisLogo')} />
      </ContainerOra>
      <ContainerForm onSubmit={handleSubmit(onFormSubmit)}>
        <Title variant="semiBold">{t('login.title')}</Title>
        <TextInput
          {...register('email')}
          label={t('login.email')}
          labelSize={isLandscapeTablet ? 'XS' : 'S'}
          typeInput="email"
          type="text"
          placeholder={t('forms:placeholders.email')}
          autoComplete="email"
          error={errors.email?.message}
        />
        <InputTextPassword
          {...register('password')}
          label={t('login.password')}
          labelSize={isLandscapeTablet ? 'XS' : 'S'}
          typeInput="password"
          type="password"
          placeholder={t('forms:placeholders.password')}
          autoComplete="current-password"
          error={
            (errors?.password?.message
              ? errors?.password?.message
              : errorsAction) as string
          }
        />
        <ContainerLinkPassword>
          <LinkPassword to={'/forgot-password'}>
            <Typography size={isLandscapeTablet ? 'XXXS' : 'XS'}>
              {t('login.forgotPassword')}
            </Typography>
          </LinkPassword>
        </ContainerLinkPassword>
        <PrincipalButton type={'submit'} text={t('login.loginButton')} />
        <LinkPolicy to={'#'}>
          <Typography size={isLandscapeTablet ? 'XXXS' : 'XS'}>
            {t('login.acceptingTerms')} <b>{t('login.privacyPolicy')}</b>
          </Typography>
        </LinkPolicy>
      </ContainerForm>
    </>
  );
}
